import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { redirectMap } from "../../../../utils/utils";
import { styles } from "../../../../theme";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const tipoMap = {
  0: { label: "HIGH", bg: styles?.logColors?.[0] || "#FEA51D" },
  1: { label: "CRITICAL", bg: styles?.logColors?.[1] || "#931A1D" },
  2: { label: "ALERT", bg: styles?.logColors?.[2] || "#D1C167" },
  3: { label: "MOVEMENTS", bg: styles?.logColors?.[3] || "#459296" },
};

const highlightKeywords = {
  red: [
    "malware",
    "metasploit",
    "zerologon",
    "ernalblue",
    "eternalblue",
    "mirai",
    "cobalt",
    "wannacry",
    "reverse shell",
    "mware",
  ],
  yellow: ["scan", "mining", "powershell", "pivot", "shellcode"],
  orange: [
    "trojan",
    "system32",
    "hkey",
    "bin",
    "sh",
    "cve",
    "polymor",
    "nbios",
    "buffer",
    "exploit",
  ],
};

const getDetailColor = (value) => {
  const text = String(value || "").toLowerCase();

  if (highlightKeywords.red.some((k) => text.includes(k))) return "error.main";
  if (highlightKeywords.yellow.some((k) => text.includes(k))) return "#D1C266";
  if (highlightKeywords.orange.some((k) => text.includes(k))) return "orange";

  return "inherit";
};

const formatIpValue = (value) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(" - ");
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((ip) => ip.trim())
      .filter(Boolean)
      .join(" - ");
  }

  return String(value);
};

const DataGridWrapper = () => {
  const [isIn, setIsIn] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const userTimezone = window.sessionStorage.getItem("timezone") || "Etc/GMT";

  useEffect(() => {
    let alive = true;

    setLoading(true);
    setError("");

    API.getHighlights({ checkInternal: isIn })
      .then((response) => {
        if (!alive) return;

        const transformed = (
          Array.isArray(response?.data) ? response.data : []
        ).map((item, index) => ({
          id: `${item?.dettaglio || "detail"}__${index}`,
          ...item,
        }));

        setData(transformed);
      })
      .catch((err) => {
        console.error("[DataGridWrapper] Failed to load highlights:", err);
        if (!alive) return;

        setData([]);
        setError(err?.response?.data?.error || err?.message || "Loading error.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [isIn]);

  const handleRedirect = useCallback(
    (tipo, details, dateFrom, dateTo, ipSource, ipDestination) => {
      window.sessionStorage.setItem("selectedTab", redirectMap[tipo].name);

      const query = new URLSearchParams({
        exact_details: details || "",
        dateFrom,
        dateTo,
      }).toString();

      if (window.sessionStorage.getItem("type") === "reseller") {
        navigate(`/reseller/myC/${redirectMap[tipo].url}?${query}`);
      } else {
        window.location.href = `/user/security/${redirectMap[tipo].url}?${query}`;
      }
    },
    [navigate]
  );

  const handleLinkClick = useCallback(
    (highlightDettaglio, tipo, ipSource, ipDestination) => {
      const lecsType = window.sessionStorage.getItem("tipoLecs");

      let dateFrom;
      let dateTo;

      if (lecsType === "LECS-CORE-SFP" || lecsType === "LECS-CORE-4-PRO") {
        dateFrom = dayjs().tz(userTimezone).subtract(24, "hour");
        dateTo = dayjs().tz(userTimezone);
      } else {
        dateFrom = dayjs().tz(userTimezone).subtract(7, "day");
        dateTo = dayjs().tz(userTimezone);
      }

      handleRedirect(
        tipo,
        highlightDettaglio,
        dateFrom.format("YYYY-MM-DDTHH:mm:ss"),
        dateTo.format("YYYY-MM-DDTHH:mm:ss"),
        ipSource,
        ipDestination
      );
    },
    [handleRedirect, userTimezone]
  );

  const columns = useMemo(
    () => [
      {
        field: "count",
        headerName: "Count",
        width: 90,
        type: "number",
        headerAlign: "left",
        align: "left",
        valueFormatter: (value) => String(value ?? 0),
      },
      {
        field: "dettaglio",
        headerName: "Detail",
        minWidth: 250,
        flex: 1.8,
        renderCell: (params) => (
          <Typography
            variant="body2"
            noWrap
            sx={{
              width: "100%",
              color: getDetailColor(params.value),
              fontWeight: 500,
            }}
            title={params.value || "-"}
          >
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "tipo",
        headerName: "Severity",
        flex: 0.9,
        minWidth: 130,
        sortable: true,
        renderCell: (params) => {
          const info = tipoMap[params.value];

          if (!info) {
            return <Chip label="UNKNOWN" size="small" variant="outlined" />;
          }

          return (
            <Chip
              label={info.label}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: info.bg,
                color: "#fff",
              }}
            />
          );
        },
      },
      {
        field: "ipSource",
        headerName: "Ips Source",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const value = formatIpValue(params.value);

          if (!value) {
            return <Chip label="N/A" size="small" variant="outlined" />;
          }

          return <Chip label={value} size="small" variant="outlined" />;
        },
      },
      {
        field: "ipDestination",
        headerName: "Ips Destination",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const value = formatIpValue(params.value);

          if (!value) {
            return <Chip label="N/A" size="small" variant="outlined" />;
          }

          return <Chip label={value} size="small" variant="outlined" />;
        },
      },
    ],
    []
  );

  return (
    <Paper
      sx={{
        p: 2,
        background: "#1e1e1e",
        borderRadius: "15px",
        height: "75vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
          Internal/External Detected Logs
        </Typography>

        <ToggleButtonGroup
          value={isIn ? "internal" : "external"}
          exclusive
          onChange={(_, v) => v != null && setIsIn(v === "internal")}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              color: "#b0b0b0",
              borderColor: "rgba(255,255,255,0.2)",
              fontSize: 11,
              py: 0.25,
              px: 1,
              textTransform: "none",
            },
            "& .Mui-selected": {
              backgroundColor: "rgba(69, 144, 149, 0.35)",
              color: "#eaeaea",
              "&:hover": {
                backgroundColor: "rgba(69, 144, 149, 0.5)",
              },
            },
          }}
        >
          <ToggleButton value="internal">Internal</ToggleButton>
          <ToggleButton value="external">External</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!error && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <DataGridPro
            density="compact"
            rows={data}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            hideFooter
            rowHeight={52}
            columnHeaderHeight={56}
            onRowClick={(params) =>
              handleLinkClick(
                params.row.dettaglio,
                params.row.tipo,
                params.row.ipSource,
                params.row.ipDestination
              )
            }
            localeText={{ noRowsLabel: "No data available." }}
            getRowClassName={(params) =>
              params?.row?.hasAnomalyMatch ? "rarest-row-anomaly-match" : ""
            }
            slotProps={{ loadingOverlay: { variant: "skeleton" } }}
            sx={{
              height: "100%",
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .rarest-row-anomaly-match": { backgroundColor: "rgba(255, 235, 59, 0.16)" },
              "& .rarest-row-anomaly-match:hover": { backgroundColor: "rgba(255, 235, 59, 0.24)" },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default DataGridWrapper;
